import React, {Fragment} from 'react';

import FocusTrap from 'focus-trap-react';
import {get, isEmpty, forEach, some, find, map} from 'lodash-es';
import PropTypes from 'prop-types';
import {PromiseState} from 'react-refetch';

import {
  GlobalNavSearchSuggestionsSection,
  SectionHeader,
  GlobalNavMenuDivider,
} from 'static/three-oh/src/components/GlobalLayout/GlobalNav/GlobalNavSearch/SearchSuggestions/SuggestionParts/SuggestionParts';
import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';

import ContentImpression from '../../../ContentImpression/ContentImpression';
import StandardSuggestion from './components/StandardSuggestion/StandardSuggestion';
import Suggestion from './components/Suggestion/Suggestion';


import styles from './styles.scss';

export default class SuggestionList extends React.Component {
  static propTypes = {
    isActive: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    isVisible: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onSuggestionClick: PropTypes.func.isRequired,
    onSuggestionFocus: PropTypes.func,
    queryTerm: PropTypes.string,
    suggestions: PropTypes.instanceOf(PromiseState).isRequired,
    user: PropTypes.object,
    isGlobalNavSearch: PropTypes.bool.isRequired,
    onSuggestionDismiss: PropTypes.func,
  };

  static defaultProps = {
    isGlobalNavSearch: false,
  };

  state = {suggestions: [], suggestionsMetaData: {}};

  /**
   * Persist suggestions in state. This allows us to continue to show
   * suggestions when the query term updates but there are no new suggestions.
   * It also prevents a UI flash for the user when the suggestions change.
   * @param {Object} props
   */
  static getDerivedStateFromProps(props) {
    const new_suggestions = get(props.suggestions, 'value.suggestions', []);
    const suggestions_meta_data = get(props.suggestions, 'value.response_meta', []);
    if (!isEmpty(new_suggestions) || !props.queryTerm) {
      return {suggestions: new_suggestions, suggestionsMetaData: suggestions_meta_data};
    }
    return null;
  }


  /**
   * Move focus to the next suggestion, if there is one.
   * @param {number} currentTabIndex - the tabIndex of the current suggestion.
   */
  focusNext = (currentTabIndex) => {
    this.tryFocusSuggestion(currentTabIndex + 1);
  }

  /**
   * Move focus to the previous suggestion, if there is one.
   * @param {number} currentTabIndex - the tabIndex of the current suggestion.
   */
  focusPrevious = (currentTabIndex) => {
    if (currentTabIndex == 1) {
      // Dismiss the search suggestions.
      this.props.onDismiss();
    }
    this.tryFocusSuggestion(currentTabIndex - 1);
  }

  /**
   * Try to focus on the suggestion with the given tabIndex. If there is no
   * suggestion with the given tabIndex, do nothing.
   * @param {number} targetTabIndex - the tabIndex of the suggestion on which to focus.
   */
  tryFocusSuggestion = (targetTabIndex) => {
    const suggestions = document.querySelectorAll('.search-suggestion');
    /** Lets shut this whole thing down if suggestions is empty to begin with. */
    if (isEmpty(suggestions)) {
      return;
    }

    forEach(suggestions, (suggestion) => {
      if (suggestion.tabIndex == targetTabIndex) {
        suggestion.focus();
      }
    });
  }

  /**
   * Render search suggestions.
   * @param {Array.<object>} suggestions - A list of objects representing search suggestions.
   * @param {Object} suggestionsMetaData - An object representing search suggestions meta data.
   * @return {Element}
   */
  renderSuggestions = (suggestions, suggestionsMetaData) => {
    const {onSuggestionClick, onSuggestionFocus, isGlobalNavSearch} = this.props;

    if (isEmpty(suggestions)) {
      return null;
    }

    const articleHeaderSuggestionsMetaData = get(suggestionsMetaData, 'article_header');
    const standardSuggestionsMetaData = get(suggestionsMetaData, 'standard');
    const articleHeaderSuggestions = get(suggestions, 'article_header');
    const showArticleHeaderSuggestions = articleHeaderSuggestions &&
      !isEmpty(articleHeaderSuggestions) &&
      some(articleHeaderSuggestions, 'text');
    const standardSuggestions = get(suggestions, 'standard');
    const showStandardSuggestions = standardSuggestions &&
      !isEmpty(standardSuggestions) &&
      find(standardSuggestions, (item) => {
        return get(item, 'display_title') && get(item, 'display_text');
      });
    const suggestionRows = map(suggestions.article_header, (item, index) => {
      if (!get(item, 'text')) {
        return null;
      }

      const searchId = articleHeaderSuggestionsMetaData && articleHeaderSuggestionsMetaData['search_id'] || '';
      const SuggestionWithTrackEvent = withTrackEvent(Suggestion);
      return (
        <ContentImpression
          model="article-header-search-suggestion"
          id={item.text}
          format="article-header-suggestion"
          eventSource="search_suggestions"
          eventProperties={{search_id: searchId}}
          key={item.text}
        >
          <SuggestionWithTrackEvent
            focusNext={this.focusNext}
            focusPrevious={this.focusPrevious}
            key={item.text}
            onClick={onSuggestionClick}
            onFocus={onSuggestionFocus}
            searchId={searchId}
            suggestion={item}
            tabIndex={index + 1}
            isGlobalNavSearch={isGlobalNavSearch}
            onSuggestionDismiss={this.props.onSuggestionDismiss}
            trackImpression
            objectType="Content"
            actionName="Search"
            eventSource="search_suggestions"
            streamProperties={{
              content_id: item.text,
              description: 'Suggested search term.',
              search_id: searchId,
            }}
          />
        </ContentImpression>
      );
    });

    const standardSuggestionRows = map(suggestions.standard, (standard, index) => {
      if (!get(standard, 'display_title') && !get(standard, 'display_text')) {
        return null;
      }

      const searchId = standardSuggestionsMetaData && standardSuggestionsMetaData['search_id'] || '';
      const StandardSuggestionWithTrackEvent = withTrackEvent(StandardSuggestion);
      return (
        <ContentImpression
          model="standards-search-suggestion"
          id={standard['content_id']}
          format="standards-suggestion"
          eventSource="search_suggestions"
          eventProperties={{search_id: searchId}}
        >
          <StandardSuggestionWithTrackEvent
            focusNext={this.focusNext}
            focusPrevious={this.focusPrevious}
            key={`${standard.text}-${index}`}
            onClick={onSuggestionClick}
            onFocus={onSuggestionFocus}
            searchId={searchId}
            suggestion={standard}
            tabIndex={suggestionRows.length + index + 1}
            isGlobalNavSearch={isGlobalNavSearch}
            onSuggestionDismiss={this.props.onSuggestionDismiss}
            trackImpression
            objectType="Content"
            actionName="Search"
            eventSource="search_suggestions"
            streamProperties={{
              content_id: standard['content_id'],
              description: 'Suggested search term.',
              search_id: searchId,
            }}
          />
        </ContentImpression>
      );
    });

    const renderArticleHeaderSuggestions = () => {
      if (!showArticleHeaderSuggestions) {
        return null;
      }

      const header = (
        isGlobalNavSearch ?
          <SectionHeader headerTitle="Suggested"/> :
            (
              <div className={styles.suggestionsHeading}>
                <h2>Suggested Searches</h2>
              </div>
            )
      );
      const articleHeaderSuggestionsSection = (
        <>
          {header}
          {suggestionRows}
        </>
      );
      const globalNavArticleHeaderSuggestionsSection = (
        <GlobalNavSearchSuggestionsSection
          header={header}
          listItems={suggestionRows}
          ariaLabel="Search Suggestions"
          dataQaSelector="search_suggestions_list"
        />
      );
      return (
        isGlobalNavSearch ?
          globalNavArticleHeaderSuggestionsSection :
          articleHeaderSuggestionsSection
      );
    };

    const renderStandardSuggestions = () => {
      if (!showStandardSuggestions) {
        return null;
      }

      const header = (
        isGlobalNavSearch ?
          <SectionHeader headerTitle="Standards"/> :
            (
              <div className={styles.suggestionsHeading}>
                <h4>Suggested Standards</h4>
              </div>
            )
      );
      const standardSuggestionsSection = (
        <>
          {header}
          {standardSuggestionRows}
        </>
      );
      const globalNavStandardSuggestionsSection = (
        <GlobalNavSearchSuggestionsSection
          header={header}
          listItems={standardSuggestionRows}
          ariaLabel="Standard Search Suggestions"
          dataQaSelector="standard_search_suggestions_list"
        />
      );

      return (
        isGlobalNavSearch ?
          globalNavStandardSuggestionsSection :
          standardSuggestionsSection
      );
    };

    const divider = (
      isGlobalNavSearch && Boolean(renderStandardSuggestions()) ?
        <GlobalNavMenuDivider/> :
        null
    );

    return (
      <>
        {renderArticleHeaderSuggestions()}
        {divider}
        {renderStandardSuggestions()}
      </>
    );
  };

  render() {
    const {
      isActive,
      onDismiss,
      isGlobalNavSearch,
      isVisible
    } = this.props;
    const {suggestions, suggestionsMetaData} = this.state;

    if (!isVisible) {
      return null;
    }

    const content = (
      isGlobalNavSearch ?
          (
            <div
              aria-label="Search Suggestions"
              className={styles.globalNavSuggestions}
              data-qa-selector="search_suggestion_list"
            >
              {this.renderSuggestions(suggestions, suggestionsMetaData)}
            </div>
          ) :
          (
            <div
              className={styles.suggestionWrapper}
              aria-label="Search Suggestions"
              data-qa-selector="search_suggestion_list"
              role="listbox"
            >
              <ul
                aria-label="Search Suggestions"
                className={styles.suggestionList}
                data-qa-selector="search_suggestion_list"
                role="listbox"
              >
                {
              this.renderSuggestions(suggestions, suggestionsMetaData)
            }
              </ul>
            </div>
          )
    );

    return (
      /** Hide RenderSuggestions container until there are results to show */
      !isEmpty(suggestions.article_header) &&
        (
          <FocusTrap
            active={isActive}
            focusTrapOptions={{
              clickOutsideDeactivates: true,
              onDeactivate: onDismiss,
            }}
          >
            {content}
          </FocusTrap>
        )
    );
  }
}
